/**
 * Stale Data Indicator Component
 *
 * Shows a subtle indicator when data is stale or cached.
 * Per plan section 2c (Offline/stale data support).
 */

import React from 'react';
import { IoCloudOffline, IoTime } from 'react-icons/io5';
import type { SectionMeta } from '../utils/dataFreshness';
import { getStaleIndicatorText, isOnline } from '../utils/dataFreshness';

interface StaleIndicatorProps {
  meta?: SectionMeta;
  className?: string;
  compact?: boolean;
}

/**
 * Shows when data is stale or there was an error fetching fresh data.
 * Compact mode shows just an icon, full mode shows icon + text.
 */
export function StaleIndicator({ meta, className = '', compact = false }: StaleIndicatorProps) {
  const indicatorText = getStaleIndicatorText(meta);
  const offline = !isOnline();

  // Show nothing if data is fresh and we're online
  if (!indicatorText && !offline) {
    return null;
  }

  // Determine which indicator to show
  const hasError = meta?.fetch_error;

  return (
    <div
      className={`stale-indicator ${hasError ? 'stale-indicator--error' : ''} ${compact ? 'stale-indicator--compact' : ''} ${className}`}
    >
      {offline ? (
        <>
          <IoCloudOffline className="stale-indicator-icon" />
          {!compact && <span className="stale-indicator-text">Offline</span>}
        </>
      ) : (
        <>
          <IoTime className="stale-indicator-icon" />
          {!compact && indicatorText && (
            <span className="stale-indicator-text">{indicatorText}</span>
          )}
        </>
      )}
    </div>
  );
}

interface OfflineBadgeProps {
  className?: string;
}

/**
 * Full-width offline notification banner.
 * Shows when the browser is offline.
 */
export function OfflineBadge({ className = '' }: OfflineBadgeProps) {
  if (isOnline()) {
    return null;
  }

  return (
    <div className={`offline-badge ${className}`}>
      <IoCloudOffline className="offline-badge-icon" />
      <span className="offline-badge-text">
        You're offline. Showing cached data.
      </span>
    </div>
  );
}

// Styles are in styles.css
